import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const MOTIVATIONAL_PROMPTS = [
  "What small act of self-care can you do today?",
  "Name one thing you're grateful for right now.",
  "What would make today feel successful?",
  "How can you show yourself compassion today?",
  "What emotion are you feeling most strongly?",
  "What boundary do you need to set today?",
  "How can you honour your needs today?",
  "What would your future self thank you for doing?",
  "What limiting belief can you challenge today?",
  "How can you bring more joy into this moment?",
  "What does your body need from you today?",
  "What fear can you acknowledge and release?",
  "How can you be kinder to yourself today?",
  "What would courage look like for you today?",
  "What pattern are you ready to break?",
];

interface DailyMotivationProps {
  userId: string;
}

export function DailyMotivation({ userId }: DailyMotivationProps) {
  const [prompt, setPrompt] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDailyPrompt();
  }, [userId]);

  const loadDailyPrompt = async () => {
    try {
      setLoading(true);
      
      // Get today's date as seed for consistent daily prompt
      const today = new Date().toISOString().split('T')[0];
      const seed = today.split('-').join('');
      const index = parseInt(seed) % MOTIVATIONAL_PROMPTS.length;
      
      setPrompt(MOTIVATIONAL_PROMPTS[index]);
      
      // Track that user viewed daily prompt
      await supabase.rpc('log_therapy_action', {
        p_user_id: userId,
        p_action: 'daily_motivation_viewed',
        p_details: {
          prompt: MOTIVATIONAL_PROMPTS[index],
          date: today
        }
      });
    } catch (error) {
      // Silent fail for daily prompt
    } finally {
      setLoading(false);
    }
  };

  const getRandomPrompt = () => {
    const randomIndex = Math.floor(Math.random() * MOTIVATIONAL_PROMPTS.length);
    setPrompt(MOTIVATIONAL_PROMPTS[randomIndex]);
  };

  if (loading) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="h-5 w-5 text-primary" />
          Daily Reflection
        </CardTitle>
        <CardDescription>A gentle prompt to guide your day</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 rounded-lg bg-background/50 border border-primary/10">
          <p className="text-lg font-light italic text-foreground">
            "{prompt}"
          </p>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={getRandomPrompt}
          className="w-full"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Get Different Prompt
        </Button>
      </CardContent>
    </Card>
  );
}
